// ProviderLogs — admin-only outbound PSP call log.
//
// Route: /admin/provider-logs, gated by RequireAuth role="admin". Backed
// by GET /v1/admin/provider-calls (admin-only). The bodies include
// account_no on AccountVerification calls — that's PII merchants must
// never see, which is why this surface is admin-tier only.
//
// Surfaces every outbound call to IDFC (GenerateToken,
// AccountVerification, InitiateFunding, CheckFundingPayout, AddFundingPayout,
// RemoveFundingPayout, ListFundingPayout) with the full request/response
// JSON, HTTP status, duration, and code/message — what would otherwise
// require SSH'ing into the host and grep'ing structured logs.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Pill } from "@/components/UI";
import { listProviderCalls, type ProviderCall } from "@/api/admin";
import { formatDateTime } from "@/utils/format";

export function ProviderLogs() {
  const [endpoint, setEndpoint] = useState<string>("");
  const [outcome, setOutcome] = useState<"all" | "success" | "failure">("all");
  const [autoRefresh, setAutoRefresh] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);

  const params = useMemo(
    () => ({
      endpoint: endpoint || undefined,
      success:
        outcome === "all" ? undefined : outcome === "success",
      limit: 100,
    }),
    [endpoint, outcome],
  );

  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["admin-provider-calls", params],
    queryFn: () => listProviderCalls(params),
    // 5s polling when the operator turns auto-refresh on — useful for
    // live diagnosis ("hit verify, watch the row appear"). Off by default
    // because the table is heavy on render-cost and the polling adds DB
    // load proportional to admin tab-count.
    refetchInterval: autoRefresh ? 5_000 : false,
  });

  const items = data?.items ?? [];
  const selected = items.find((it) => it.id === selectedId) ?? null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">admin · diagnostics</div>
          <h1>Provider call log</h1>
          <div className="sub">
            Every outbound PSP call captured server-side. Use this to
            triage upstream failures without SSH'ing into the host.
          </div>
        </div>
        <div className="spacer" />
        <button
          className="btn ghost sm"
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* ── Filters ─────────────────────────────────────────────── */}
      <div
        className="card flat"
        style={{ marginTop: 12, padding: "12px 16px" }}
      >
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <label
              className="dim"
              style={{ fontSize: 12 }}
              htmlFor="endpoint-filter"
            >
              Endpoint
            </label>
            <select
              id="endpoint-filter"
              className="input sm"
              value={endpoint}
              onChange={(e) => setEndpoint(e.target.value)}
              style={{ minWidth: 180 }}
            >
              <option value="">All endpoints</option>
              <option value="GenerateToken.php">GenerateToken</option>
              <option value="AccountVerification.php">
                AccountVerification (penny-drop)
              </option>
              <option value="AddFundingPayout.php">AddFundingPayout</option>
              <option value="InitiateFunding.php">InitiateFunding</option>
              <option value="CheckFundingPayout.php">CheckFundingPayout</option>
              <option value="RemoveFundingPayout.php">
                RemoveFundingPayout
              </option>
              <option value="ListFundingPayout.php">ListFundingPayout</option>
            </select>
          </div>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <label
              className="dim"
              style={{ fontSize: 12 }}
              htmlFor="outcome-filter"
            >
              Outcome
            </label>
            <select
              id="outcome-filter"
              className="input sm"
              value={outcome}
              onChange={(e) =>
                setOutcome(e.target.value as "all" | "success" | "failure")
              }
            >
              <option value="all">All</option>
              <option value="success">Success only</option>
              <option value="failure">Failures only</option>
            </select>
          </div>

          <label
            style={{
              display: "flex",
              gap: 6,
              alignItems: "center",
              fontSize: 12,
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (5s)
          </label>

          <div className="spacer" />
          <span className="dim" style={{ fontSize: 11 }}>
            {items.length} {items.length === 1 ? "call" : "calls"} shown
            {data?.limit && items.length === data.limit && " (capped)"}
          </span>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────── */}
      <div className="card flat" style={{ marginTop: 12 }}>
        {isLoading ? (
          <div className="empty">
            <p className="dim">Loading provider calls…</p>
          </div>
        ) : isError ? (
          <div className="empty">
            <p>Could not load the provider call log.</p>
          </div>
        ) : items.length === 0 ? (
          <div className="empty">
            <p>
              No provider calls match these filters yet. Make a payout
              or run penny-drop verify and refresh.
            </p>
          </div>
        ) : (
          <table className="data" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th style={{ width: 170 }}>Time</th>
                <th style={{ width: 200 }}>Endpoint</th>
                <th style={{ width: 80 }}>HTTP</th>
                <th style={{ width: 60 }}>Code</th>
                <th>Message</th>
                <th style={{ width: 70, textAlign: "right" }}>Duration</th>
                <th style={{ width: 90 }}>Outcome</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <ProviderCallRow
                  key={row.id}
                  row={row}
                  onSelect={() => setSelectedId(row.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selected && (
        <ProviderCallModal
          call={selected}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function ProviderCallRow({
  row,
  onSelect,
}: {
  row: ProviderCall;
  onSelect: () => void;
}) {
  const message =
    row.transport_error ||
    row.provider_message ||
    row.provider_status ||
    "";
  return (
    <tr
      onClick={onSelect}
      style={{ cursor: "pointer" }}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onSelect();
      }}
    >
      <td className="dim" style={{ fontSize: 11 }}>
        {formatDateTime(row.created_at)}
      </td>
      <td className="mono" style={{ fontSize: 12 }}>
        {row.endpoint}
      </td>
      <td>
        <StatusBadge status={row.http_status} />
      </td>
      <td className="mono" style={{ fontSize: 11 }}>
        {row.provider_code ?? "—"}
      </td>
      <td
        style={{
          fontSize: 12,
          maxWidth: 0,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        title={message}
      >
        {message || "—"}
      </td>
      <td
        className="mono dim"
        style={{ fontSize: 11, textAlign: "right" }}
      >
        {row.duration_ms}ms
      </td>
      <td>
        <Pill tone={row.success ? "pos" : "neg"}>
          {row.success ? "Success" : "Fail"}
        </Pill>
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: number }) {
  if (status === 0) {
    return (
      <span
        className="mono"
        style={{ fontSize: 11, color: "var(--bad, #c33)" }}
        title="No HTTP response — transport-level failure"
      >
        TX-FAIL
      </span>
    );
  }
  return (
    <span
      className="mono"
      style={{
        fontSize: 11,
        color: status >= 400 ? "var(--bad, #c33)" : "inherit",
      }}
    >
      {status}
    </span>
  );
}

// ─── Detail modal ────────────────────────────────────────────────────────────

function ProviderCallModal({
  call,
  onClose,
}: {
  call: ProviderCall;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg, white)",
          border: "1px solid var(--hair)",
          borderRadius: 6,
          padding: 20,
          maxWidth: 900,
          width: "90vw",
          maxHeight: "85vh",
          overflow: "auto",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <span className="mono" style={{ fontSize: 14, fontWeight: 500 }}>
            {call.endpoint}
          </span>
          <span className="dim" style={{ fontSize: 11 }}>
            {formatDateTime(call.created_at)}
          </span>
          <Pill tone={call.success ? "pos" : "neg"}>
            {call.success ? "Success" : "Fail"}
          </Pill>
          <div className="spacer" />
          <button className="btn ghost sm" type="button" onClick={onClose}>
            <I.X size={12} />
          </button>
        </div>

        {/* Quick summary grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: 12,
            marginTop: 16,
            marginBottom: 16,
          }}
        >
          <Cell label="HTTP" value={call.http_status === 0 ? "TX-FAIL" : String(call.http_status)} />
          <Cell label="Provider code" value={call.provider_code ?? "—"} />
          <Cell label="Provider status" value={call.provider_status ?? "—"} />
          <Cell label="Duration" value={`${call.duration_ms}ms`} />
          <Cell label="Request ID" value={call.request_id ?? "—"} mono />
          {call.bank_account_id != null && (
            <Cell label="Bank rail" value={String(call.bank_account_id)} mono />
          )}
        </div>

        {call.provider_message && (
          <div
            style={{
              padding: 10,
              border: "1px solid var(--hair)",
              borderRadius: 4,
              marginBottom: 16,
              background: "var(--bg-sunk, #f7f7f8)",
              fontSize: 12,
            }}
          >
            <div className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>
              Provider message
            </div>
            <div>{call.provider_message}</div>
          </div>
        )}

        {call.transport_error && (
          <div
            style={{
              padding: 10,
              border: "1px solid var(--bad, #c33)",
              borderRadius: 4,
              marginBottom: 16,
              background: "var(--bad-soft, #fee)",
              color: "var(--bad, #c33)",
              fontSize: 12,
            }}
          >
            <div className="eyebrow" style={{ fontSize: 10, marginBottom: 4 }}>
              Transport error
            </div>
            <div className="mono">{call.transport_error}</div>
          </div>
        )}

        <BodyBlock label="Request body" body={call.request_body} />
        <BodyBlock label="Response body" body={call.response_body} />
      </div>
    </div>
  );
}

function Cell({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div className="eyebrow" style={{ fontSize: 10 }}>
        {label}
      </div>
      <div className={mono ? "mono" : undefined} style={{ fontSize: 12 }}>
        {value}
      </div>
    </div>
  );
}

function BodyBlock({ label, body }: { label: string; body: string }) {
  return (
    <div style={{ marginTop: 12 }}>
      <div
        className="eyebrow"
        style={{
          fontSize: 10,
          letterSpacing: "0.06em",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <pre
        className="mono"
        style={{
          fontSize: 11,
          background: "var(--bg-sunk, #f7f7f8)",
          border: "1px solid var(--hair)",
          borderRadius: 4,
          padding: 10,
          overflow: "auto",
          maxHeight: 280,
          margin: 0,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {pretty(body)}
      </pre>
    </div>
  );
}

// pretty attempts to JSON-parse + re-stringify the body for readability.
// Falls back to verbatim when it's not valid JSON (transport-error rows,
// truncation markers, etc.).
function pretty(body: string): string {
  if (!body) return "";
  try {
    return JSON.stringify(JSON.parse(body), null, 2);
  } catch {
    return body;
  }
}
